import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import NearYouPopup from './components/NearYouPopup';
import Homepage from './pages/Homepage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import AuthCallback from './pages/AuthCallback';
import PickRole from './pages/PickRole';
import Tournaments from './pages/Tournaments';
import TournamentDetails from './pages/TournamentDetails';
import CityTournaments from './pages/CityTournaments';
import OrganizerProfile from './pages/OrganizerProfile';
import PlayerDashboard from './pages/PlayerDashboard';
import EditProfile from './pages/EditProfile';
import OrganizerDashboard from './pages/OrganizerDashboard';
import CreateTournament from './pages/CreateTournament';
import EditTournament from './pages/EditTournament';
import TournamentRegistrations from './pages/TournamentRegistrations';
import AdminDashboard from './pages/AdminDashboard';
import About from './pages/About';
import FAQ from './pages/FAQ';
import HowToList from './pages/HowToList';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import './App.css';
import { Toaster } from 'sonner';

// Stable allowedRoles arrays (extracted to prevent prop reference churn)
const ROLES_PLAYER = ['player'];
const ROLES_ORGANIZER = ['organizer'];
const ROLES_PLAYER_OR_ORGANIZER = ['player', 'organizer'];
const ROLES_ADMIN = ['admin'];

const DashboardRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center text-gray-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'organizer') return <Navigate to="/organizer" replace />;
  return <Navigate to="/player-dashboard" replace />;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <Navbar />
            <Routes>
              <Route path="/" element={<Homepage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/pick-role" element={<PickRole />} />
              <Route path="/tournaments" element={<Tournaments />} />
              <Route path="/tournaments/city/:city" element={<CityTournaments />} />
              <Route path="/tournaments/:id" element={<TournamentDetails />} />
              <Route path="/organizer-profile/:id" element={<OrganizerProfile />} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/how-to-list" element={<HowToList />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />

              <Route path="/dashboard" element={<DashboardRedirect />} />

              <Route path="/player-dashboard" element={<ProtectedRoute allowedRoles={ROLES_PLAYER}><PlayerDashboard /></ProtectedRoute>} />
              <Route path="/profile/edit" element={<ProtectedRoute allowedRoles={ROLES_PLAYER_OR_ORGANIZER}><EditProfile /></ProtectedRoute>} />

              <Route path="/organizer" element={<ProtectedRoute allowedRoles={ROLES_ORGANIZER}><OrganizerDashboard /></ProtectedRoute>} />
              <Route path="/organizer/create-tournament" element={<ProtectedRoute allowedRoles={ROLES_ORGANIZER}><CreateTournament /></ProtectedRoute>} />
              <Route path="/organizer/tournaments/:id/edit" element={<ProtectedRoute allowedRoles={ROLES_ORGANIZER}><EditTournament /></ProtectedRoute>} />
              <Route path="/organizer/tournaments/:id/registrations" element={<ProtectedRoute allowedRoles={ROLES_ORGANIZER}><TournamentRegistrations /></ProtectedRoute>} />

              <Route path="/admin" element={<ProtectedRoute allowedRoles={ROLES_ADMIN}><AdminDashboard /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Footer />
            <NearYouPopup />
            <Toaster position="bottom-right" />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
